import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Error formatter for API responses
export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // null = checking, false = not authenticated
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/auth/me`, {
                withCredentials: true
            });
            setUser(response.data);
        } catch (error) {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        try {
            const response = await axios.post(`${API_URL}/api/auth/login`, {
                email,
                password
            }, { withCredentials: true });
            setUser(response.data);
            return { success: true, user: response.data };
        } catch (error) {
            const message = formatApiErrorDetail(error.response?.data?.detail);
            return { success: false, error: message };
        }
    };

    const register = async (data) => {
        try {
            const response = await axios.post(`${API_URL}/api/auth/register`, data, {
                withCredentials: true
            });
            setUser(response.data);
            return { success: true, user: response.data };
        } catch (error) {
            const message = formatApiErrorDetail(error.response?.data?.detail);
            return { success: false, error: message };
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API_URL}/api/auth/logout`, {}, {
                withCredentials: true
            });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser(false);
        }
    };

    const forgotPassword = async (email) => {
        try {
            await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
            return { success: true };
        } catch (error) {
            const message = formatApiErrorDetail(error.response?.data?.detail);
            return { success: false, error: message };
        }
    };

    const resetPassword = async (token, newPassword) => {
        try {
            await axios.post(`${API_URL}/api/auth/reset-password`, {
                token,
                new_password: newPassword
            });
            return { success: true };
        } catch (error) {
            const message = formatApiErrorDetail(error.response?.data?.detail);
            return { success: false, error: message };
        }
    };

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        forgotPassword,
        resetPassword,
        checkAuth
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
